import type { Transaction } from "@/types/transaction";
import { formatSignedCurrency } from "@/lib/currency";

/**
 * Money arriving, under the same two sign conventions the backend uses
 * (`apps.transactions.queries.is_inflow_row`): Plaid writes money in as NEGATIVE,
 * the demo seed (transaction ids `demo-{uid}-…`) writes it as POSITIVE.
 */
export function isInflow(t: Pick<Transaction, "transaction_id" | "amount">): boolean {
  const amount = Number(t.amount);
  return (t.transaction_id ?? "").startsWith("demo-") ? amount > 0 : amount < 0;
}

/**
 * What a row is, for its sign, arrow and label (FP-055). The sign is the money's
 * direction, never the income flag: contract batch 1a (SC18/CAT-003) made a Venmo
 * transfer in, a loan payout and a refund *not income*, and the rows still took
 * their sign from `is_income`, so money received printed "-$60.00" as spending
 * while the summary above them (`total_expenses`, F7) already netted it as money
 * back. The backend only sets `is_income` on an inflow (`income_flag_for`), so
 * every income row keeps its "+".
 */
export type RowKind = "income" | "money-in" | "expense";

export function rowKind(t: Pick<Transaction, "transaction_id" | "amount" | "is_income">): RowKind {
  if (!isInflow(t)) return "expense";
  return t.is_income ? "income" : "money-in";
}

export const ROW_KIND_LABEL: Record<RowKind, string> = {
  income: "Income",
  "money-in": "Money in",
  expense: "Expense",
};

/**
 * The selection's net, signed: positive is money in (contract batch 5). The assign
 * modal used to add every row's magnitude, so a $100 purchase and its $100 refund
 * read as $200 (classification, "Total" row: "stop adding income and expenses").
 */
export function selectedTotal(rows: Pick<Transaction, "transaction_id" | "amount">[]): number {
  const cents = rows.reduce((sum, t) => {
    const magnitude = Math.round(Math.abs(Number(t.amount)) * 100);
    return sum + (isInflow(t) ? magnitude : -magnitude);
  }, 0);
  return cents / 100;
}

const fmt = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

/** The window Transactions' summary totals cover, from the page's own date filter. */
export function summaryWindow(filters: { dateFrom?: string; dateTo?: string }): string {
  const { dateFrom, dateTo } = filters;
  if (dateFrom && dateTo) return `${fmt(dateFrom)} – ${fmt(dateTo)}`;
  if (dateFrom) return `since ${fmt(dateFrom)}`;
  if (dateTo) return `through ${fmt(dateTo)}`;
  return "all time";
}

/**
 * The Expenses total with the sign the right way round (BEN-141 F7). Refunds net
 * against spending (contract batch 5), so under a narrow filter the server's
 * `total_expenses` can go negative: more money came back than went out. The card
 * printed "-" before `formatCurrency`, which signs a negative itself, so a net $50
 * refund read "--$50.00". Spending is "-$X", money back is "+$X", and nothing is
 * "$0.00". Mobile's `formatExpensesTotal` is the same rule.
 */
export function formatExpensesTotal(total: number): string {
  // CALL-06: a total that rounds to zero is "$0.00", unsigned.
  return formatSignedCurrency(-total, { cents: true });
}
